import React, { useEffect, useState } from 'react';
import { fetchDataFreshness } from '../../api/phase2Analytics';
import { useAuth } from '../../context/AuthContext';
import { useTenantView } from '../../context/TenantViewContext';
import { mapDataFreshnessToView } from '../../lib/mapPhase2Analytics';
import { cn } from '../../lib/utils';

function statusDetail(item) {
  if (item.status === 'error') return 'error';
  if (item.status === 'empty') return 'no data';
  if (item.status === 'stale') {
    if (item.lagSeconds == null) return 'delayed';
    const hours = Math.round(item.lagSeconds / 3600);
    if (hours >= 1) return `${hours}h delay`;
    const mins = Math.max(1, Math.round(item.lagSeconds / 60));
    return `${mins}m delay`;
  }
  if (item.lastEventAt) {
    const mins = Math.max(1, Math.round((Date.now() - new Date(item.lastEventAt).getTime()) / 60000));
    if (mins < 60) return `${mins}m ago`;
    return `${Math.round(mins / 60)}h ago`;
  }
  return 'fresh';
}

function statusTone(status) {
  if (status === 'error') return 'text-rose-700';
  if (status === 'stale') return 'text-amber-800';
  if (status === 'empty') return 'text-slate-400';
  return 'text-slate-600';
}

function StatusDot({ status }) {
  return (
    <span
      className={cn(
        'mt-px h-1.5 w-1.5 shrink-0 rounded-full',
        status === 'fresh' && 'bg-emerald-500',
        status === 'stale' && 'bg-amber-500',
        status === 'empty' && 'bg-slate-300',
        status === 'error' && 'bg-rose-500'
      )}
      aria-hidden="true"
    />
  );
}

/**
 * Quiet shell-level source freshness row — metadata, not a badge strip.
 */
export function DataFreshnessBanner({ className }) {
  const { isSuperAdmin } = useAuth();
  const { viewingTenant } = useTenantView();
  const accountId = isSuperAdmin && viewingTenant?.id ? viewingTenant.id : null;

  const [view, setView] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const { data, source } = await fetchDataFreshness({ accountId });
        if (cancelled) return;
        setView(mapDataFreshnessToView(data, source));
      } catch {
        if (!cancelled) setView(null);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [accountId]);

  if (!view?.sources?.length) return null;

  return (
    <ul
      className={cn(
        'flex min-w-0 flex-wrap items-baseline justify-end gap-x-0 gap-y-1',
        className
      )}
      role="status"
      aria-label="Data freshness by source"
    >
      {view.sources.map((item, index) => {
        const detail = statusDetail(item);
        return (
          <li key={item.source} className="inline-flex items-baseline">
            {index > 0 ? (
              <span className="mx-2.5 text-slate-300 select-none" aria-hidden="true">
                ·
              </span>
            ) : null}
            <span className="inline-flex items-center gap-1.5" title={item.label}>
              <StatusDot status={item.status} />
              <span className="text-[12px] font-medium tracking-tight text-slate-700">
                {item.source}
              </span>
              <span className={cn('text-[12px] tabular-nums', statusTone(item.status))}>
                {detail}
              </span>
            </span>
          </li>
        );
      })}
    </ul>
  );
}
